import {
  Box,
  ButtonProps,
  Grid,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { GridAddIcon, GridDeleteIcon } from "@mui/x-data-grid";
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { ReactComponent as Export } from "../../assets/icons/export.svg";
import { COLORS } from "../../assets/theme";
import TruncatedText from "../../components/common/TruncatedText";
import config from "../../config.json";

import Actions from "../../components/common/Actions";
import CustomDialog from "../../components/common/CustomDialog";
import CustomLabel from "../../components/common/CustomLabel";
import CustomTable from "../../components/common/table/CustomTable";
import SearchHeaderContainer from "../../components/common/table/SearchHeaderContainer";
import ToastNotification from "../../components/ToastNotification";

import {
  createOperator,
  deleteOperatorApi,
  editOperatorApi,
  exportOperator,
  fetchOperator,
  fetchOperatorHistory,
  IOperator,
  uploadOperatorExcel,
  useIOperatorQuery,
} from "../../services/OperatorsService";

import { useQueryClient } from "@tanstack/react-query";
import CustomCheckbox from "../../components/CustomCheckbox";
import FileUploadField from "../../components/FileUploadField";
import {
  fetchCountries,
  fetchCountry,
  getCountryNameOptions,
} from "../../services/CountryService";
import { IField, IPageable, Params } from "../../types/table/interfaces.type";
import CustomTextField from "../../components/common/CustomTextField";
import ExportTable from "../../components/ExportTable";

import History from "../../components/History";
import SearchForm from "../../components/common/SearchForm";
import { loadOperatorsByCountry } from "../../utils/commonDataUtils";
import AutoComplete, { IAutoCompleteProps } from "../../components/common/AutoComplete";
import { IExport } from "../../services/CostPlanService";
import GetErrors from "../../components/GetErrors";
import { hasJwtPermission } from "../../services/ServicesHelpers";

type Option = {
  label: string;
  value: string | number;
};

const OperatorsPage: React.FC = () => {
  const queryClient = useQueryClient();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const [appliedFilters, setAppliedFilters] = useState<Params<IOperator>>({
    search: "",
  });
  const [operatorsByCountry, setOperatorsByCountry] = useState<Option[]>([]);
  const [isOperatorDisabled, setIsOperatorDisabled] = useState(true);
  const [filters, setFilters] = useState<
    Params<IOperator & { countryId: number; operatorId: string }>
  >({});

  const [countriesOptions, setCountriesOptions] = useState<Option[]>([]);

  const handleOpenDialog = (
    mode: "add" | "edit" | "delete" | "history",
    id?: string
  ) => {
    dialogRef.current?.openDialog(mode, id);
  };
  const handleOpenImportDialog = () => {
    dialogRef.current?.openImportDialog();
  };

  const [selectedRowIds, setSelectedRowIds] = useState<string[]>([]);

  useEffect(() => {
    setSelectedRowIds([]);
  }, [filters]);

  const onRowSelect = (row: IOperator, checked: boolean) => {
    console.log(`Row ID ${row.id} selection changed: ${checked}`);
    setSelectedRowIds((prevSelected) => {
      const newSelected = checked
        ? [...prevSelected, String(row.id)]
        : prevSelected.filter((id) => id !== String(row.id));
      console.log("Selected row IDs:", newSelected);
      return newSelected;
    });
  };

  const isRowSelected = (row: IOperator) =>
    selectedRowIds.includes(String(row.id));

  const dialogRef = useRef<OperatorDialogRef>(null);

  useEffect(() => {
    const fetchCountriesData = async () => {
      try {
        const countriesPage = await fetchCountries({
          sort: "name",
          size: config.MAX_PAGE_SIZE,
        });
        const countriesAsOptions = pageAsOptions(countriesPage);
        setCountriesOptions(countriesAsOptions);
      } catch (error: any) {
        ToastNotification.error("Failed to fetch countries. Please try again.");
      }
    };

    fetchCountriesData();
  }, []);

  const handleSearchChange = (search: string) => {
    setAppliedFilters((prev) => ({ ...prev, search }));
  };

  const actions = Actions({
    handleOpenEditModal: {
      handler: async (id: string | number) => {
        handleOpenDialog("edit", String(id));
      },
      isDisabled: !hasJwtPermission("GET_OPERATOR"),
    },
    onViewHistory: {
      // isDisabled: true,
      handler: async (id: string | number) => {
        handleOpenDialog("history", String(id));
      },
      isDisabled: !hasJwtPermission("GET_ALL_OPERATORS_HISTORY"),
    },
  });

  const fields: IField<IOperator>[] = [
    { key: "name", header: "Operator" },
    {
      key: "countryId",
      header: "Country",
      options: (t) => getCountryNameOptions(t.countryId),
    },
    {
      key: "cc",
      header: "CC",
    },
    {
      key: "mcc",
      header: "MCC",
      transform: async (t) => t.mcc.join(","),
    },
    {
      key: "mnc",
      header: "MNC",
      transform: async (t) => <TruncatedText text={t.mnc?.join(",")} />,
    },
  ];

  const handleSearchClick = () => {
    setAppliedFilters((prev) => ({
      ...prev,
      ...(filters.operatorId
        ? { id: Number(filters.operatorId) }
        : { OperatorId: undefined }),
      ...(filters.countryId
        ? { countryId: filters.countryId }
        : { countryId: undefined }),
    }));
    setSelectedRowIds([]);
  };

  const buttons = [
    {
      label: "Search",
      variant: "contained" as ButtonProps["variant"],
      bgColor: COLORS.GOLD,
      onClick: handleSearchClick,
      permission: "GET_ALL_OPERATORS",
    },
  ];

  const autoCompletes: IAutoCompleteProps[] = [
    {
      label: "Country",
      value: filters.countryId ? String(filters.countryId) : "",
      options: countriesOptions,
      placeholder: "Select Country",
      clearable: true,
      onChange: async (value: string | number | (string | number)[]) => {
        const selectedCountry = countriesOptions.find(
          (option) => option.value === value
        );

        setFilters((prev) => ({
          ...prev,
          countryId: selectedCountry
            ? Number(selectedCountry.value)
            : undefined,
          operatorId: undefined,
        }));

        if (selectedCountry) {
          try {
            setIsOperatorDisabled(true);
            const operators = await loadOperatorsByCountry(
              Number(selectedCountry.value)
            );
            setOperatorsByCountry(operators);
          } catch (error: any) {
            ToastNotification.error(error.message);
            setOperatorsByCountry([]);
          } finally {
            setIsOperatorDisabled(false);
          }
        } else {
          setOperatorsByCountry([]);
          setIsOperatorDisabled(true);
        }
      },
    },
    {
      label: "Operator",
      value: filters.operatorId ? String(filters.operatorId) : "",
      options: operatorsByCountry,
      disabled: isOperatorDisabled,
      clearable: true,
      placeholder: "Select Operator",
      onChange: (value: string | number | (string | number)[]) => {
        setFilters((prev) => ({
          ...prev,
          operatorId: value ? String(value) : undefined,
        }));
      },
    },
  ];

  //     {
  //       label: "Name",
  //       value: filters.name === undefined ? "" : String(filters.name),
  //       options: countriesOptions,
  //       onChange: (value: string | number | (string | number)[]) => {
  //         const val = getAutoCompleteValue(value);
  //         setFilters((prev) => ({ ...prev, name: val }));
  //       },
  //       clearable: true,
  //     },
  //     {
  //       label: "Account Manager",
  //      value: filters.operatorId === undefined ? "" : String(filters.operatorId),
  //     options: operatorsByCountry,
  //  onChange: (value: string | number | (string | number)[]) => {
  //       const selectedOperator = operatorsByCountry.find(
  //         (option) => option.value === value
  //       );

  //       setFilters((prev) => ({
  //         ...prev,
  //         operatorId: Number(selectedOperator?.value),
  //       }));
  //     },
  //       clearable: true,
  //     },

  //   ];
  return (
    <Box padding={isSmallScreen ? 2 : 3}>
      <Box sx={{ mb: 3 }}>
        <SearchForm autoCompletes={autoCompletes} buttons={buttons} />
      </Box>
      <CustomLabel
        text="Operators List"
        variant={isSmallScreen ? "h6" : "h5"}
        fontWeight={500}
        marginBottom={1}
      />

      <SearchHeaderContainer
        search={appliedFilters.search ?? ""}
        setSearch={handleSearchChange}
        buttonConfig={[
          {
            size: "small",
            label: "Import Operators",
            onClick: () => handleOpenImportDialog(),
            textColor: COLORS.GOLD,
            variant: "outlined",
            borderColor: COLORS.GOLD,
            permission: "IMPORT_OPERATOR",
          },
          {
            size: "small",
            bgColor: COLORS.GOLD,
            startIcon: <GridAddIcon />,
            onClick: () => handleOpenDialog("add"),
            textColor: COLORS.iconColor,
            permission: "POST_OPERATOR",
          },
          {
            size: "small",
            bgColor: COLORS.GOLD,
            variant: "outlined" as ButtonProps["variant"],
            startIcon: <GridDeleteIcon />,
            onClick: () => handleOpenDialog("delete"),
            textColor: COLORS.iconColor,
            permission: "DELETE_OPERATOR",
          },
          {
            size: "small",
            bgColor: COLORS.GOLD,
            startIcon: <Export />,
            onClick: async () => {
              try {
                await exportOperator(appliedFilters);
                ToastNotification.success("Export completed successfully!");
              } catch (error: any) {
                ToastNotification.error(
                  error.message || "Export failed. Please try again."
                );
              }
            },
            showExportProgress: true,
            textColor: COLORS.iconColor,
            label: "Export",
            permission: "GET_ALL_OPERATORS_CSV",
          },
        ]}
      />
      <CustomTable<IOperator>
        filters={appliedFilters}
        fields={fields}
        fetchFunc={useIOperatorQuery}
        actions={actions}
        checkboxSelect={true}
        onRowSelect={onRowSelect}
        isRowSelected={isRowSelected}
        sortableFields={["name"]}
      />
      <OperatorDialog
        ref={dialogRef}
        onRefresh={() => {
          setAppliedFilters((prev) => ({ ...prev }));
          queryClient.invalidateQueries();
        }}
        selectedRowIds={selectedRowIds}
        onClearSelection={() => setSelectedRowIds([])}
        countriesOptions={countriesOptions}
        appliedFilters={appliedFilters}
      />
    </Box>
  );
};

function pageAsOptions(page: IPageable<{ id: any; name: string }>): Option[] {
  return page.content.map((element) => ({
    label: element.name,
    value: element.id,
  }));
}
export interface OperatorDialogRef {
  openDialog: (
    mode: "add" | "edit" | "delete" | "history",
    id?: string
  ) => void;
  openImportDialog: () => void;
}

interface OperatorDialogProps {
  onRefresh: () => void;
  countriesOptions: Option[];
  selectedRowIds: string[];
  appliedFilters: Params<IOperator>;
  onClearSelection: () => void;
}

const OperatorDialog = forwardRef<OperatorDialogRef, OperatorDialogProps>(
  (
    {
      onRefresh,
      countriesOptions,
      selectedRowIds,
      onClearSelection,
      appliedFilters,
    },
    ref
  ) => {
    const [dialogOpen, setDialogOpen] = useState(false);
    const [dialogMode, setDialogMode] = useState<
      "add" | "edit" | "delete" | "history"
    >("add");
    const [selectedId, setSelectedId] = useState<number | undefined>();
    const [dialogData, setDialogData] = useState<Partial<IOperator>>({
      mcc: [],
      mnc: [],
    });
    useEffect(() => console.log(dialogData), [dialogData]);
    const isSaveDisabled = !dialogData.name;
    const [importDialogOpen, setImportDialogOpen] = useState(false);
    const [importFile, setImportFile] = useState<File | null>(null);
    const [isUploading, setIsUploading] = useState(false);
    const [errors, setErrors] = useState<Record<string, boolean>>({});
    const [selectedExport, setSelectedExport] = useState<IExport | null>(null);
    const [errorDetailsDialogOpen, setErrorDetailsDialogOpen] = useState(false);
    const [textFields, setTextFields] = useState({
      cc: "CC",
      mnc: "MNC",
      mcc: "MCC",
      operator: "Operator",
      country: "Country",
    });
    const validateField = (field: string, value: string | number) => {
      setErrors((prevErrors) => ({
        ...prevErrors,
        [field]: !value,
      }));
    };

    useImperativeHandle(ref, () => ({
      openDialog: (mode, id: string | number | undefined) => {
        setDialogMode(mode);
        setSelectedId(Number(id));
        setDialogOpen(true);
        if (mode && id) {
          loadEditData(Number(id));
        }
      },
      openImportDialog: () => {
        setImportDialogOpen(true);
      },
    }));

    const resetDialogData = () => {
      setDialogData({});
      setDialogOpen(false);
    };

    const handleViewErrorDetails = (exportData: IExport) => {
      setSelectedExport(exportData);
      setErrorDetailsDialogOpen(true);
      setImportDialogOpen(false);
    };

    const handleCloseErrorDetails = () => {
      setErrorDetailsDialogOpen(false);
      setSelectedExport(null);
    };

    const loadEditData = async (id: number) => {
      try {
        const operatorId = Number(id);

        const operatorData = await fetchOperator(operatorId);

        setDialogData(operatorData);
      } catch (error: any) {
        console.error("Error loading operator data:", error);
        ToastNotification.error(
          "Failed to load operator data. Please try again."
        );
      }
    };

    const handleSave = async () => {
      if (isSaveDisabled) {
        ToastNotification.error("Please fill all required fields.");
        return;
      }

      if (dialogMode === "add") {
        await createOperator(dialogData as IOperator);
        ToastNotification.success("Operator added successfully!");
        resetDialogData();
      } else if (dialogMode === "edit" && selectedId) {
        await editOperatorApi(selectedId, dialogData as IOperator);

        ToastNotification.success("Operator updated successfully!");

        resetDialogData();
      }

      onRefresh();
      setDialogOpen(false);
    };

    const handleDelete = async () => {
      try {
        await deleteOperatorApi(selectedRowIds);
        ToastNotification.success("Operator deleted successfully!");
        onClearSelection();
        onRefresh();
        setDialogOpen(false);
      } catch {
        ToastNotification.error("Error deleting operator.");
      }
    };
    const queryClient = useQueryClient();

    const handleUpload = async () => {
      if (!importFile) {
        ToastNotification.error("Please select a file.");
        return;
      }
      setIsUploading(true);
      try {
        const override = false;

        await uploadOperatorExcel(importFile, override, textFields);
        queryClient.invalidateQueries({ queryKey: ["importedFiles"] });
        queryClient.refetchQueries({ queryKey: ["importedFiles"] });
        ToastNotification.success("File uploaded successfully!");
        setDialogData({});
        onRefresh();
      } catch (error: any) {
        console.error("Error uploading file:", error);
        ToastNotification.error(error.response?.data?.message || error.message);
      } finally {
        setIsUploading(false);
      }
    };

    const fieldHistory: IField<IOperator & { historyTimestamp: Date }>[] = [
      {
        key: "name",
        header: "Operator Name",
        transform: (t) => t.name,
      },
      {
        key: "countryId",
        header: "Country",
        options: (t) => getCountryNameOptions(Number(t.countryId)),
      },
      {
        key: "cc",
        header: "CC",
      },
      {
        key: "mcc",
        header: "MCC",
        transform: async (t) =>
          Array.isArray(t.mcc)
            ? (t.mcc as (string | number)[]).join(",")
            : t.mcc ??
              (Array.isArray(t.mcc)
                ? (t.mcc as (string | number)[]).join(",")
                : t.mcc),
      },
      {
        key: "mnc",
        header: "MNC",
        transform: async (t) => (
          <TruncatedText
            text={
              Array.isArray(t.mnc)
                ? (t.mnc as (string | number)[]).join(",")
                : t.mnc
            }
          />
        ),
      },
      {
        key: "historyTimestamp",
        header: "Date Modified",
      },
      { key: "createdBy", header: "Created By" },
      { key: "updatedBy", header: "Updated By" },
    ];

    return (
      <>
        <CustomDialog
          open={dialogOpen}
          onClose={resetDialogData}
          mode={dialogMode}
          title={
            dialogMode === "history"
              ? "History"
              : dialogMode === "add"
              ? "Add New Operator"
              : dialogMode === "edit"
              ? "Edit Operator"
              : "Delete Operator"
          }
          fields={
            dialogMode === "history"
              ? [
                  {
                    type: "custom",
                    component: (
                      <History
                        filters={appliedFilters}
                        fields={fieldHistory}
                        fetchFunc={async ({ size = 10 }) => {
                          const result = await fetchOperatorHistory(
                            Number(selectedId),
                            {
                              size,
                            }
                          );
                          return {
                            ...result,
                            content: result.content.map((item) => ({
                              ...item,
                              historyTimestamp: new Date(item.historyTimestamp),
                            })),
                          };
                        }}
                      />
                    ),
                  },
                ]
              : dialogMode === "delete"
              ? [
                  {
                    type: "custom",
                    component: (
                      <Box>Are you sure you want to delete this operator?</Box>
                    ),
                  },
                ]
              : [
                  {
                    type: "custom" as const,
                    component: (
                      <AutoComplete
                        label="Country"
                        value={dialogData.countryId ?? ""}
                        options={countriesOptions}
                        onChange={async (
                          value: string | number | (string | number)[]
                        ) => {
                          const countryId = Array.isArray(value)
                            ? Number(value[0])
                            : Number(value);

                          setDialogData((prev) => ({
                            ...prev,
                            countryId,
                          }));
                          validateField("countryId", countryId);

                          // Only fetch country data if a value is selected
                          if (countryId && !isNaN(countryId)) {
                            try {
                              const country = await fetchCountry(countryId);
                              setDialogData((prev) => ({
                                ...prev,
                                mcc: country?.mcc ?? [],
                              }));
                            } catch (error: any) {
                              ToastNotification.error(
                                "Failed to fetch MCC. Please try again."
                              );
                            }
                          } else {
                            // Clear MCC when country is cleared
                            setDialogData((prev) => ({
                              ...prev,
                              mcc: [],
                            }));
                          }
                        }}
                        clearable={true}
                        disablePortal={false}
                        error={!!errors.countryId}
                        placeholder="Select Country"
                        highlightLabelStar={true}
                        helperText={
                          errors.countryId ? "Country is required" : ""
                        }
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            backgroundColor: COLORS.WhiteBackground,
                          },
                        }}
                      />
                    ),
                  },
                  {
                    type: "text" as const,
                    label: "Operator Name",
                    bgColor: COLORS.WhiteBackground,
                    highlightLabelStar: true,
                    value: dialogData.name ?? "",
                    onChange: (value: string | number) => {
                      setDialogData((prev) => ({
                        ...prev,
                        name: String(value),
                      }));
                      validateField("name", String(value));
                    },
                    placeholder: "Enter Name",
                    error: !!errors.name,
                    helperText: errors.name ? "Name is required" : "",
                  },
                  {
                    type: "text" as const,
                    label: "CC",
                    bgColor: COLORS.WhiteBackground,
                    highlightLabelStar: true,
                    value: dialogData.cc ?? "",
                    onChange: (value: string | number) => {
                      setDialogData((prev) => ({
                        ...prev,
                        cc: String(value),
                      }));
                      validateField("cc", String(value));
                    },
                    placeholder: "Enter CC",
                    error: !!errors.cc,
                    helperText: errors.cc ? "CC is required" : "",
                    disabled: false,
                  },
                  {
                    type: "text" as const,
                    label: "MCC",
                    bgColor: COLORS.WhiteBackground,
                    highlightLabelStar: true,
                    value: (dialogData.mcc || []).join(","),
                    onChange: (value: string | number) => {
                      setDialogData((prev) => ({
                        ...prev,
                        mcc: String(value)
                          .replaceAll(/[^0-9,]/g, "")
                          .split(","),
                      }));
                      validateField("mcc", String(value));
                    },
                    placeholder: "Enter MCC",
                    error: !!errors.mcc,
                    helperText: errors.mcc ? "MCC is required" : "",
                    disabled: false,
                  },
                  {
                    type: "text" as const,
                    label: "MNC",
                    bgColor: COLORS.WhiteBackground,
                    highlightLabelStar: false,
                    value: (dialogData.mnc || []).join(","),
                    onChange: (value: string | number) => {
                      setDialogData((prev) => ({
                        ...prev,
                        mnc: String(value)
                          .replaceAll(/[^0-9,]/g, "")
                          .split(","),
                      }));
                      validateField("mnc", String(value));
                    },
                    placeholder: "Enter MNC",
                  },
                  {
                    type: "custom" as const,
                    component: (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "flex-start",
                          gap: 1,
                          width: "100%",
                        }}
                      >
                        <CustomCheckbox
                          checked={!dialogData.isDeleted}
                          onChange={(event) =>
                            setDialogData((prev) => ({
                              ...prev,
                              isDeleted: !event.target.checked,
                            }))
                          }
                          ariaLabel="Active"
                        />
                        <Box>
                          <Typography>Active</Typography>
                        </Box>
                      </Box>
                    ),
                  },
                ]
          }
          buttons={
            dialogMode === "history"
              ? []
              : [
                  dialogMode === "delete"
                    ? {
                        label: "Delete",
                        onClick: handleDelete,
                        bgColor: COLORS.GOLD,
                        permission: "DELETE_OPERATOR",
                      }
                    : {
                        label: "Save",
                        onClick: handleSave,
                        disabled: isSaveDisabled || (dialogMode === "edit" ? !hasJwtPermission("PUT_OPERATOR") : !hasJwtPermission("POST_OPERATOR")),
                        bgColor: COLORS.GOLD,
                      },
                ]
          }
        />
        <CustomDialog
          open={importDialogOpen}
          onClose={() => setImportDialogOpen(false)}
          title="Import Operators"
          type="import"
          dialogSx={{
            minWidth: {
              xs: "100vw",
              sm: "560px",
              md: "760px",
              lg: "980px",
              xl: "1100px",
            },
            maxWidth: {
              xs: "100vw",
              sm: "90vw",
              md: "85vw",
              lg: "75vw",
              xl: "1100px",
            },
            width: "100%",
            maxHeight: "88vh",
            overflow: "hidden",
          }}
          contentSx={{
            maxHeight: {
              xs: "55vh",
              sm: "60vh",
            },
            overflowY: "auto",
            paddingBottom: "8px",
          }}
          fields={[
            {
              type: "custom",
              component: (
                <Box>
                  <FileUploadField
                    title="File"
                    accept=".xlsx,.xls,.csv"
                    placeholder="Add Excel file"
                    onFileChange={setImportFile}
                  />
                  <Box sx={{ mt: { xs: 1.5, sm: 2 } }}>
                    <Typography
                      variant="subtitle1"
                      sx={{
                        fontWeight: "bold",
                        fontSize: { xs: "1rem", sm: "1.1rem" },
                      }}
                    >
                      Excel Header Fields
                    </Typography>
                  </Box>

                  <Grid
                    container
                    spacing={{ xs: 1.5, sm: 2 }}
                    sx={{
                      overflowX: "auto",
                      width: "100%",
                      mt: { xs: 0.5, sm: 1 },
                    }}
                    key="text-fields-row"
                  >
                    <Grid
                      item
                      xs={12}
                      sm={6}
                      md={4}
                      component={"div" as React.ElementType}
                    >
                      <CustomTextField
                        label="Operator Header"
                        placeholder="Operator Header"
                        size="small"
                        highlightLabelStar
                        bgColor={COLORS.WhiteBackground}
                        value={textFields.operator}
                        onChange={(e) =>
                          setTextFields({
                            ...textFields,
                            operator: e.target.value,
                          })
                        }
                        fullWidth
                      />
                    </Grid>
                    <Grid
                      item
                      xs={12}
                      sm={6}
                      md={4}
                      component={"div" as React.ElementType}
                    >
                      <CustomTextField
                        label="Country Header"
                        placeholder="Country Header"
                        highlightLabelStar
                        size="small"
                        bgColor={COLORS.WhiteBackground}
                        value={textFields.country}
                        onChange={(e) =>
                          setTextFields({
                            ...textFields,
                            country: e.target.value,
                          })
                        }
                        fullWidth
                      />
                    </Grid>
                    <Grid
                      item
                      xs={12}
                      sm={6}
                      md={4}
                      component={"div" as React.ElementType}
                    >
                      <CustomTextField
                        label="CC Header"
                        placeholder="CC Header"
                        size="small"
                        highlightLabelStar
                        bgColor={COLORS.WhiteBackground}
                        value={textFields.cc}
                        onChange={(e) =>
                          setTextFields({
                            ...textFields,
                            cc: e.target.value,
                          })
                        }
                        fullWidth
                      />
                    </Grid>
                    <Grid
                      item
                      xs={12}
                      sm={6}
                      md={4}
                      component={"div" as React.ElementType}
                    >
                      <CustomTextField
                        label="MCC Header"
                        placeholder="MCC Header"
                        size="small"
                        highlightLabelStar
                        bgColor={COLORS.WhiteBackground}
                        value={textFields.mcc}
                        onChange={(e) =>
                          setTextFields({
                            ...textFields,
                            mcc: e.target.value,
                          })
                        }
                        fullWidth
                      />
                    </Grid>
                    <Grid
                      item
                      xs={12}
                      sm={6}
                      md={4}
                      component={"div" as React.ElementType}
                    >
                      <CustomTextField
                        label="MNC Header"
                        placeholder="MNC Header"
                        size="small"
                        highlightLabelStar
                        bgColor={COLORS.WhiteBackground}
                        value={textFields.mnc}
                        onChange={(e) =>
                          setTextFields({
                            ...textFields,
                            mnc: e.target.value,
                          })
                        }
                        fullWidth
                      />
                    </Grid>
                  </Grid>
                </Box>
              ),
            },
            {
              type: "custom",
              component: (
                <Box
                  sx={{
                    width: "100%",
                    px: { xs: 1, sm: 2 },
                    mt: { xs: 1, sm: 2 },
                  }}
                >
                  <ExportTable
                    usedBy="OPERATOR"
                    onView={handleViewErrorDetails}
                    onCloseImportDialog={() => setImportDialogOpen(false)}
                  />
                </Box>
              ),
            },
          ]}
          buttons={[
            {
              label: "Upload",
              onClick: handleUpload,
              permission: "IMPORT_OPERATOR",
            },
          ]}
          mode="add"
        />

        <CustomDialog
          open={errorDetailsDialogOpen}
          onClose={handleCloseErrorDetails}
          title="View Error Details"
          mode="history"
          fields={[
            {
              type: "custom",
              component: (
                <GetErrors
                  usedBy="OPERATOR"
                  fields={[
                    { key: "rowNumber", header: "Row Number" },
                    { key: "errorMessage", header: "Error Message" },
                    { key: "createdBy", header: "Created By" },
                    { key: "createdDate", header: "Created Date" },
                  ]}
                  exportId={String(selectedExport)}
                />
              ),
            },
          ]}
          buttons={[{ label: "Close", onClick: handleCloseErrorDetails }]}
        />
      </>
    );
  }
);

export default OperatorsPage;
